if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      }
      this.mql = window.matchMedia('(min-width: 750px)');
      this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.prependMedia = this.dataset.disablePrepend != 'true';
      if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
      this.filteringOption = this.dataset.filteringOption;
      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }


    // Keep the mobile gallery as tall as the currently selected media.
    connectedCallback() {
      this.ssHeightFrame = requestAnimationFrame(() => this.ssInitHeight());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.ssHeightFrame);
      this.ssResizeObserver?.disconnect();
      this.elements.viewer?.removeEventListener('slideChanged', this.ssSlideListener);
      this.elements.viewer?.removeEventListener('load', this.ssScheduleHeight, true);
      this.elements.viewer?.removeEventListener('loadedmetadata', this.ssScheduleHeight, true);
      window.removeEventListener('resize', this.ssScheduleHeight);
      this.ssRestoreHeight();
      this.ssHeightReady = false;
    }

    ssInitHeight() {
      if (this.ssHeightReady || !this.isConnected) return;
      this.ssList = this.elements.viewer?.querySelector('[id^="Slider-Gallery-"]');
      if (!this.ssList) return;
      this.ssHeightReady = true;
      this.ssOriginalStyles = new Map();
      this.ssScheduleHeight = () => {
        cancelAnimationFrame(this.ssHeightFrame);
        this.ssHeightFrame = requestAnimationFrame(() => this.ssUpdateHeight());
      };
      this.ssSlideListener = (event) => {
        const slide = event.detail?.currentElement;
        if (slide?.parentElement === this.ssList) this.ssCurrentSlide = slide;
        this.ssScheduleHeight();
      };
      this.elements.viewer.addEventListener('slideChanged', this.ssSlideListener);
      this.elements.viewer.addEventListener('load', this.ssScheduleHeight, true);
      this.elements.viewer.addEventListener('loadedmetadata', this.ssScheduleHeight, true);
      window.addEventListener('resize', this.ssScheduleHeight);
      if ('ResizeObserver' in window) {
        this.ssResizeObserver = new ResizeObserver(this.ssScheduleHeight);
        this.ssResizeObserver.observe(this.ssList);
        this.ssList.querySelectorAll('.product-media-container').forEach(
          media => this.ssResizeObserver.observe(media)
        );
      }
      this.ssUpdateHeight();
    }

    ssSetStyle(element, property, value) {
      if (!this.ssOriginalStyles.has(element)) this.ssOriginalStyles.set(element, new Map());
      const saved = this.ssOriginalStyles.get(element);
      if (!saved.has(property)) saved.set(property, [
        element.style.getPropertyValue(property),
        element.style.getPropertyPriority(property)
      ]);
      if (element.style.getPropertyValue(property) !== value ||
          element.style.getPropertyPriority(property) !== 'important') {
        element.style.setProperty(property, value, 'important');
      }
    }

    ssRestoreHeight() {
      this.ssOriginalStyles?.forEach((properties, element) => {
        properties.forEach(([value, priority], property) => {
          if (value) element.style.setProperty(property, value, priority);
          else element.style.removeProperty(property);
        });
      });
      this.ssOriginalStyles?.clear();
    }

    ssUpdateHeight() {
      if (!this.ssList || !this.isConnected) return;
      // Preserve desktop and multi-column/mobile grid layouts.
      const layout = getComputedStyle(this.ssList);
      if (this.mql.matches || layout.display !== 'flex' || layout.flexWrap !== 'nowrap') {
        this.ssRestoreHeight();
        return;
      }
      const slides = Array.from(this.ssList.children).filter(
        slide => slide.matches('.product__media-item')
      );
      const visible = slide => slide && slide.parentElement === this.ssList &&
        !slide.classList.contains('hidden') && slide.getClientRects().length &&
        getComputedStyle(slide).display !== 'none';
      let active = this.ssCurrentSlide;
      if (!visible(active)) active = slides.find(slide => visible(slide) && slide.classList.contains('is-active'));
      if (!active) active = slides.find(visible);
      if (!active) return;

      this.ssSetStyle(this.ssList, 'align-items', 'flex-start');
      this.ssSetStyle(this.ssList, 'overflow-y', 'hidden');
      if (this.ssList.scrollTop !== 0) this.ssList.scrollTop = 0;
      slides.forEach(slide => {
        this.ssSetStyle(slide, 'height', 'auto');
        this.ssSetStyle(slide, 'align-self', 'flex-start');
        const videoContainer = slide.querySelector('.product-media-container.media-type-video, .product-media-container.media-type-external_video');
        if (videoContainer) {
          this.ssSetStyle(videoContainer, 'width', '70%');
          this.ssSetStyle(videoContainer, 'margin-left', 'auto');
          this.ssSetStyle(videoContainer, 'margin-right', 'auto');
        }
      });
      const media = active.querySelector('.product-media-container');
      if (!media) return;
      const slideRect = active.getBoundingClientRect();
      const mediaRect = media.getBoundingClientRect();
      const slideStyle = getComputedStyle(active);
      const number = value => parseFloat(value) || 0;
      const contentHeight = Math.max(slideRect.height, mediaRect.bottom - slideRect.top);
      if (contentHeight < 1) return;
      let height = contentHeight + number(slideStyle.marginTop) + number(slideStyle.marginBottom);
      const listStyle = getComputedStyle(this.ssList);
      if (listStyle.boxSizing === 'border-box') {
        height += number(listStyle.paddingTop) + number(listStyle.paddingBottom) +
          number(listStyle.borderTopWidth) + number(listStyle.borderBottomWidth);
      }
      this.ssSetStyle(this.ssList, 'min-height', '0px');
      this.ssSetStyle(this.ssList, 'height', Math.ceil(height) + 'px');
    }

    onSlideChanged(event) {
      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${ event.detail.currentElement.dataset.mediaId }"]`);
      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
      if (filtering && currentVariant) {
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .slider-counter__link');
        allSlides.forEach(slide => {
          if (slide.dataset.alt === currentVariant[this.filteringOption] || slide.dataset.alt === 'always_display') {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        })
      }

      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${ mediaId }"]`);
      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');
      this.ssCurrentSlide = activeMedia;
      this.ssScheduleHeight?.();

      if (prepend && this.prependMedia) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({behavior: 'smooth'});
        }
      });
      this.playActiveMedia(activeMedia);

      if (filtering && currentVariant) {
        if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
        if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();
      }

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${ mediaId }"]`);
      this.setActiveThumbnail(activeThumbnail);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);
      if (this.elements.thumbnails.isSlideVisible(thumbnail, false, 10, true)) return;

      if (this.elements.thumbnails.vertical) {
        console.log('setActiveThumbnail')
        this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
      } else {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }
  });
}